import { apiClient } from './client';
import type { AuthUser, SelectRoleResponse } from '../types/api.types';

/**
 * Si el tenant del usuario tiene requireOtp=false, el backend hace bypass
 * de OTP y devuelve el JWT directo (token/user) en vez de pedir código.
 */
export async function requestOtp(idToken: string, tenantId?: string): Promise<
  { phoneNumber: string } | { token: string; user: AuthUser; needsRoleSelection?: boolean }
> {
  const res = await apiClient.post('/api/auth/google', { idToken, tenantId });
  return res.data;
}

export async function verifyOtp(phoneNumber: string, otp: string): Promise<{ token: string; user: AuthUser; needsRoleSelection?: boolean }> {
  const res = await apiClient.post('/api/auth/verify-otp', { phoneNumber, otp });
  return res.data;
}

/** Acceso directo al tenant demo (solo dev) — sin Google ni OTP */
export async function loginDemo(): Promise<{ token: string; user: AuthUser }> {
  const res = await apiClient.get('/api/auth/demo');
  return res.data;
}

/** Selecciona el rol activo para la sesión actual */
export async function selectRole(role: string): Promise<SelectRoleResponse> {
  const res = await apiClient.post('/api/auth/select-role', { role });
  return res.data;
}

/**
 * Inicia sesión con número de teléfono y contraseña.
 * Si el tenant tiene doble factor habilitado, devuelve `requiresOtp: true`
 * en vez del token — el flujo debe continuar llamando a `verifyOtp` con el
 * mismo `phoneNumber` y el código recibido por Telegram.
 */
export async function loginWithPassword(
  phoneNumber: string, password: string, tenantId?: string
): Promise<
  | { token: string; user: AuthUser; needsRoleSelection?: boolean; requiresOtp?: false }
  | { requiresOtp: true; phoneNumber: string; message: string }
> {
  const res = await apiClient.post('/api/auth/login-password', { phoneNumber, password, tenantId });
  return res.data;
}

/**
 * Inicio de sesión con correo y contraseña dentro de una empresa (POST /api/auth/login).
 * Mismas respuestas que loginWithPassword: sesión directa o paso de código por Telegram.
 */
export async function loginWithEmail(
  tenantId: string, email: string, password: string
): Promise<
  | { token: string; user: AuthUser; needsRoleSelection?: boolean; requiresOtp?: false }
  | { requiresOtp: true; phoneNumber: string; message: string }
> {
  const res = await apiClient.post('/api/auth/login', { tenantId, email, password });
  return res.data;
}

/** Solicita código para recuperación de contraseña (vía Telegram o email) */
export interface PasswordResetRequestResult {
  message: string;
  /** ¿Se emitió un código en esta petición? (false: en espera o bloqueado) */
  sent?: boolean;
  /** Segundos que faltan para poder pedir otro código. */
  retryAfterSeconds?: number;
  /** Se agotaron las peticiones: un administrador debe desbloquear. */
  locked?: boolean;
  used?: number;
  max?: number;
}

export async function requestPasswordReset(identifier: string, method: 'telegram' | 'email' = 'telegram'): Promise<PasswordResetRequestResult> {
  const res = await apiClient.post('/api/auth/request-password-reset', { identifier, phoneNumber: identifier, method });
  return res.data;
}

/** Valida código OTP y establece nueva contraseña */
export async function resetPassword(
  identifier: string,
  code: string,
  newPassword: string
): Promise<{ message: string }> {
  const res = await apiClient.post('/api/auth/reset-password', { identifier, phoneNumber: identifier, code, newPassword });
  return res.data;
}
